import { useState, useEffect, useCallback } from 'react';
import { casesApi } from '../api/cases';
import type { Case } from '../types';

const DEFAULT_PAGE_SIZE = 15;

interface UseOtherCasesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  enabled?: boolean; // Only fetch when this section is active
}

interface UseOtherCasesReturn {
  cases: Case[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
  search: string;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setSearch: (search: string) => void;
  refresh: () => Promise<void>;
}

export function useOtherCases({
  page: initialPage = 1,
  pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
  search: initialSearch = '',
  enabled = true,
}: UseOtherCasesParams = {}): UseOtherCasesReturn {
  const [cases, setCases] = useState<Case[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [search, setSearch] = useState(initialSearch);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCases = useCallback(async () => {
    if (!enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await casesApi.getOtherCases({
        page,
        pageSize,
        search: search || undefined,
      });

      setCases(response.cases);
      setTotal(response.total);
      setTotalPages(response.total_pages);
    } catch (err) {
      console.error('Failed to fetch other cases:', err);
      setError('Failed to fetch other cases');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, enabled]);

  // Fetch on mount and when params change
  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // Reset to page 1 when search changes
  const handleSetSearch = useCallback((newSearch: string) => {
    setSearch(newSearch);
    setPage(1);
  }, []);

  // Reset to page 1 when page size changes
  const handleSetPageSize = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  return {
    cases,
    total,
    page,
    pageSize,
    totalPages,
    isLoading,
    error,
    search,
    setPage,
    setPageSize: handleSetPageSize,
    setSearch: handleSetSearch,
    refresh: fetchCases,
  };
}
